import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ExternalLink, Loader2, BarChart3 } from 'lucide-react'
import type { PerClassMetric, PerRuleMetric, TestRun, TestSetListItem } from '@sophia/shared-schemas'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { EmptyState } from '@/components/ui/EmptyState'
import { testSetsApi, testSetsQueryKey } from '@/features/test-sets/api'
import { PrecisionHint } from '@/features/test-sets/components/PrecisionHint'
import { explainClassPrecision, explainRulePrecision } from '@/features/test-sets/lib/precision-explainer'

interface Props {
  classId: string
  className: string
}

function pct(v: number | null | undefined, digits = 1): string {
  if (v == null || Number.isNaN(v)) return '—'
  return `${(v * 100).toFixed(digits)}%`
}

function precisionTone(v: number): 'success' | 'warning' | 'danger' {
  if (v >= 0.98) return 'success'
  if (v >= 0.9) return 'warning'
  return 'danger'
}

interface AggregatedRun {
  testSet: TestSetListItem
  latestRun: TestRun
  perClass: PerClassMetric | null
  perRuleForClass: PerRuleMetric[]
}

export function ClassPerformanceTab({ classId, className }: Props) {
  const { data: list, isLoading } = useQuery({
    queryKey: testSetsQueryKey.list(),
    queryFn: testSetsApi.list,
  })

  // Pra cada test set, busca detalhes pra pegar a última run completa
  const testSetIds = useMemo(() => (list ?? []).map((t) => t.id), [list])
  const detailQueries = useQuery({
    queryKey: ['class-performance', classId, testSetIds],
    queryFn: async () => {
      const details = await Promise.all(testSetIds.map((id) => testSetsApi.detail(id)))
      return details
    },
    enabled: testSetIds.length > 0,
  })

  if (isLoading || detailQueries.isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-fg-3">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    )
  }

  const aggregated: AggregatedRun[] = []
  for (const ts of list ?? []) {
    const detail = detailQueries.data?.find((d) => d.testSet.id === ts.id)
    const latestRun = detail?.runs.find((r) => r.status === 'COMPLETED' && r.metrics) ?? null
    if (!latestRun) continue
    const perClass = latestRun.metrics?.perClass.find((p) => p.classId === classId) ?? null
    const perRuleForClass = latestRun.metrics?.perRule.filter((r) => r.classId === classId) ?? []
    if (perClass || perRuleForClass.length > 0) {
      aggregated.push({ testSet: ts, latestRun, perClass, perRuleForClass })
    }
  }

  if (aggregated.length === 0) {
    return (
      <EmptyState
        icon={BarChart3}
        title="Sem dados de performance ainda"
        description={`Pra ver como "${className}" performa, importe uma planilha de teste em "Bases de teste" e rode contra as regras atuais.`}
        action={
          <Link
            to="/benchmarks"
            className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md bg-brand-600 hover:bg-brand-700 text-white text-base font-medium transition-colors"
          >
            Ir pra Bases de teste
          </Link>
        }
      />
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {aggregated.map(({ testSet, latestRun, perClass, perRuleForClass }) => (
        <Card key={testSet.id}>
          <CardHeader>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h3 className="font-display font-bold text-base text-fg-1">{testSet.name}</h3>
                <div className="text-xs text-fg-3 mt-0.5 data-mono">
                  {testSet.totalRows} linhas · última run{' '}
                  {new Date(latestRun.startedAt).toLocaleString('pt-BR')}
                </div>
              </div>
              <Link
                to={`/benchmarks/${testSet.id}?class=${classId}`}
                className="inline-flex items-center gap-1.5 text-xs text-brand-700 hover:text-brand-800 underline"
              >
                Ver no benchmark
                <ExternalLink className="h-3 w-3" />
              </Link>
            </div>
          </CardHeader>
          <CardBody>
            {!perClass || perClass.total === 0 ? (
              <p className="text-sm text-fg-3 italic">
                Esta classe não tem linhas nesta planilha.
                {perRuleForClass.length > 0 &&
                  ` Mas ${perRuleForClass.length} regra(s) desta classe rodaram (pode ter dado falso positivo).`}
              </p>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="px-3 py-2 rounded-md border border-border-subtle">
                    <div className="text-2xs uppercase tracking-wider text-fg-3">Total</div>
                    <div className="data-mono text-lg font-bold text-fg-1">{perClass.total}</div>
                  </div>
                  <div className="px-3 py-2 rounded-md border border-border-subtle">
                    <div className="text-2xs uppercase tracking-wider text-fg-3">Acertou</div>
                    <div className="data-mono text-lg font-bold text-success">{perClass.correctlyPredicted}</div>
                  </div>
                  <div className="px-3 py-2 rounded-md border border-border-subtle">
                    <div className="text-2xs uppercase tracking-wider text-fg-3">Errou</div>
                    <div className="data-mono text-lg font-bold text-danger">{perClass.misclassified}</div>
                  </div>
                  <div className="px-3 py-2 rounded-md border border-border-subtle">
                    <div className="text-2xs uppercase tracking-wider text-fg-3">Não classif.</div>
                    <div className="data-mono text-lg font-bold text-warning">{perClass.notClassified}</div>
                  </div>
                </div>
                {(() => {
                  const precisionExplanation = explainClassPrecision({
                    total: perClass.total,
                    predictedCount: perClass.predictedCount,
                    correctlyPredicted: perClass.correctlyPredicted,
                  })
                  const precisionValue = precisionExplanation?.kind === 'unavailable' ? '—' : pct(perClass.precision)
                  return (
                    <>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-fg-3">Precisão</span>
                        <Badge tone={precisionTone(perClass.precision)}>
                          <span className="data-mono">{precisionValue}</span>
                        </Badge>
                        <span className="text-fg-3 ml-2">Recall</span>
                        <Badge tone={precisionTone(perClass.recall)}>
                          <span className="data-mono">{pct(perClass.recall)}</span>
                        </Badge>
                        <span className="text-fg-3 ml-2">F1</span>
                        <Badge tone={precisionTone(perClass.f1)}>
                          <span className="data-mono">{pct(perClass.f1)}</span>
                        </Badge>
                      </div>
                      <PrecisionHint explanation={precisionExplanation} />
                    </>
                  )
                })()}
              </>
            )}

            {perRuleForClass.length > 0 && (
              <div className="mt-4">
                <div className="text-2xs uppercase tracking-wider text-fg-3 mb-2">
                  Regras desta classe nesta run
                </div>
                <ul className="flex flex-col gap-1">
                  {perRuleForClass.map((r) => {
                    const precisionExplanation = explainRulePrecision({
                      matches: r.matches,
                      correctMatches: r.correctMatches,
                    })
                    const precisionValue = precisionExplanation?.kind === 'unavailable' ? '—' : pct(r.precision)
                    return (
                      <li
                        key={r.ruleId}
                        className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-border-subtle"
                      >
                        <span className="text-sm text-fg-2 truncate">{r.ruleName}</span>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-2xs text-fg-3 data-mono">
                            {r.correctMatches}/{r.matches} matches
                          </span>
                          <PrecisionHint explanation={precisionExplanation} compact />
                          <Badge
                            tone={
                              r.confidence === 'ALTA'
                                ? 'success'
                                : r.confidence === 'MEDIA'
                                  ? 'warning'
                                  : 'danger'
                            }
                          >
                            {precisionValue} · {r.confidence}
                          </Badge>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              </div>
            )}
          </CardBody>
        </Card>
      ))}
    </div>
  )
}
